import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Typography,
  Card,
  List,
  Tag,
  Progress,
  Alert,
  Space,
  Input,
  Button,
  Statistic,
  Row,
  Col,
  message
} from 'antd';
import {
  ExclamationCircleOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { BatchInfo } from '../types';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface BatchDeleteConfirmModalProps {
  visible: boolean;
  batches: BatchInfo[];
  onConfirm: (reason?: string) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

interface BatchDeleteInfo extends BatchInfo {
  hasReviewed: boolean;
  reviewedCount: number;
  totalCount: number;
  riskLevel: 'low' | 'medium' | 'high';
}

interface DeleteProgress {
  current: number;
  total: number;
  currentBatch?: string;
  errors: Array<{
    batchId: string;
    error: string;
  }>;
}

const BatchDeleteConfirmModal: React.FC<BatchDeleteConfirmModalProps> = ({
  visible,
  batches,
  onConfirm,
  onCancel,
  loading = false
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [step, setStep] = useState<'confirm' | 'progress' | 'result'>('confirm');
  const [progress, setProgress] = useState<DeleteProgress>({ current: 0, total: 0, errors: [] });
  const [batchesInfo, setBatchesInfo] = useState<BatchDeleteInfo[]>([]);

  const loadBatchesInfo = useCallback(async () => {
    // 模拟获取批次详细信息，包括审核状态
    const info: BatchDeleteInfo[] = batches.map(batch => {
      // 模拟数据：根据批次ID判断风险等级
      const hasReviewed = Math.random() > 0.5;
      const reviewedCount = hasReviewed ? Math.floor(Math.random() * batch.record_count) : 0;
      const riskLevel: 'low' | 'medium' | 'high' =
        hasReviewed ? 'high' :
        batch.record_count > 100 ? 'medium' : 'low';

      return {
        ...batch,
        hasReviewed,
        reviewedCount,
        totalCount: batch.record_count,
        riskLevel
      };
    });
    setBatchesInfo(info);
  }, [batches]);

  // 重置状态
  useEffect(() => {
    if (visible) {
      setStep('confirm');
      setConfirmText('');
      setDeleteReason('');
      setProgress({ current: 0, total: 0, errors: [] });
      // 模拟获取批次详细信息
      loadBatchesInfo();
    }
  }, [visible, batches, loadBatchesInfo]);

  const getRiskColor = (level: 'low' | 'medium' | 'high') => {
    switch (level) {
      case 'low': return '#52c41a';
      case 'medium': return '#faad14';
      case 'high': return '#ff4d4f';
    }
  };

  const getRiskText = (level: 'low' | 'medium' | 'high') => {
    switch (level) {
      case 'low': return '低风险';
      case 'medium': return '中风险';
      case 'high': return '高风险';
    }
  };

  const getOverallRisk = () => {
    if (batchesInfo.some(b => b.riskLevel === 'high')) return 'high';
    if (batchesInfo.some(b => b.riskLevel === 'medium')) return 'medium';
    return 'low';
  };

  const totalRecords = batchesInfo.reduce((sum, batch) => sum + batch.totalCount, 0);
  const totalReviewedRecords = batchesInfo.reduce((sum, batch) => sum + batch.reviewedCount, 0);
  const hasReviewedBatches = batchesInfo.some(batch => batch.hasReviewed);

  const isConfirmValid = () => {
    if (getOverallRisk() === 'high') {
      return confirmText === '确认删除' && deleteReason.trim().length >= 10;
    }
    return confirmText === '确认删除';
  };

  const handleConfirm = async () => {
    if (!isConfirmValid()) {
      message.error('请完成所有确认步骤');
      return;
    }

    setStep('progress');
    setProgress({ current: 0, total: batches.length, errors: [] });

    try {
      // 调用确认回调，传递删除原因
      await onConfirm(deleteReason);

      setProgress(prev => ({ ...prev, current: batches.length }));
      setStep('result');
    } catch (error) {
      console.error('删除过程中发生错误:', error);
      message.error('删除过程中发生错误');
      setStep('confirm');
    }
  };

  const renderConfirmStep = () => (
    <div>
      {/* 风险警告 */}
      <Alert
        message={`删除风险等级：${getRiskText(getOverallRisk())}`}
        description={
          <div>
            <Paragraph>
              您即将删除 <Text strong>{batches.length}</Text> 个批次，
              总计 <Text strong>{totalRecords}</Text> 条记录。
              {hasReviewedBatches && (
                <Text type="danger">
                  其中包含 <Text strong>{totalReviewedRecords}</Text> 条已审核记录，删除后无法恢复！
                </Text>
              )}
            </Paragraph>
          </div>
        }
        type={getOverallRisk() === 'high' ? 'error' : getOverallRisk() === 'medium' ? 'warning' : 'info'}
        icon={getOverallRisk() === 'high' ? <ExclamationCircleOutlined /> : <InfoCircleOutlined />}
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* 批次列表 */}
      <Card title="待删除批次详情" size="small" style={{ marginBottom: 16 }}>
        <List
          size="small"
          dataSource={batchesInfo}
          renderItem={(batch) => (
            <List.Item>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Space>
                  <Text code>{batch.batch_id}</Text>
                  <Text>{batch.company_name}</Text>
                  <Tag color={getRiskColor(batch.riskLevel)}>
                    {getRiskText(batch.riskLevel)}
                  </Tag>
                </Space>
                <Space>
                  <Text type="secondary">记录数：{batch.totalCount}</Text>
                  {batch.hasReviewed && (
                    <Tag color="red" icon={<CheckCircleOutlined />}>
                      已审核：{batch.reviewedCount}
                    </Tag>
                  )}
                  <Text type="secondary">{batch.import_date}</Text>
                </Space>
              </Space>
            </List.Item>
          )}
        />
      </Card>

      {/* 统计信息 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic title="批次数量" value={batches.length} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="总记录数" value={totalRecords} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="已审核记录"
              value={totalReviewedRecords}
              valueStyle={{ color: totalReviewedRecords > 0 ? '#ff4d4f' : undefined }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="风险等级" value={getRiskText(getOverallRisk())} />
          </Card>
        </Col>
      </Row>

      {/* 高风险删除需要填写原因 */}
      {getOverallRisk() === 'high' && (
        <Card title="删除原因" size="small" style={{ marginBottom: 16 }}>
          <Alert
            message="高风险删除操作"
            description="由于此次删除包含已审核记录，请详细说明删除原因（至少10个字符）"
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
          />
          <TextArea
            rows={3}
            placeholder="请详细说明删除这些批次的原因..."
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
          />
          <Text type="secondary">
            已输入 {deleteReason.length} / 10 字符
          </Text>
        </Card>
      )}

      {/* 确认输入 */}
      <Card title="确认删除" size="small">
        <Alert
          message="请输入 '确认删除' 以继续"
          description="此操作不可撤销，请谨慎操作"
          type="error"
          showIcon
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder="请输入：确认删除"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          style={{ marginBottom: 8 }}
        />
        {confirmText && confirmText !== '确认删除' && (
          <Text type="danger">输入不匹配，请输入 "确认删除"</Text>
        )}
      </Card>
    </div>
  );

  const renderProgressStep = () => (
    <div>
      <Alert
        message="正在删除批次数据"
        description="请勿关闭窗口，删除过程中请耐心等待..."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Card title="删除进度" size="small" style={{ marginBottom: 16 }}>
        <Progress
          percent={Math.round((progress.current / progress.total) * 100)}
          status={progress.errors.length > 0 ? 'exception' : 'active'}
          strokeColor={{
            '0%': '#108ee9',
            '100%': '#87d068',
          }}
        />
        <Paragraph style={{ marginTop: 8 }}>
          删除进度：{progress.current} / {progress.total}
          {progress.currentBatch && (
            <Text type="secondary"> - 当前：{progress.currentBatch}</Text>
          )}
        </Paragraph>
      </Card>

      {progress.errors.length > 0 && (
        <Card title="删除错误" size="small">
          <List
            size="small"
            dataSource={progress.errors}
            renderItem={(error) => (
              <List.Item>
                <Text type="danger">
                  <ExclamationCircleOutlined /> {error.batchId}: {error.error}
                </Text>
              </List.Item>
            )}
          />
        </Card>
      )}
    </div>
  );

  const renderResultStep = () => (
    <div>
      <Alert
        message={progress.errors.length === 0 ? "删除完成" : "删除完成（部分失败）"}
        description={
          progress.errors.length === 0
            ? `成功删除 ${batches.length} 个批次，共 ${totalRecords} 条记录`
            : `成功删除 ${batches.length - progress.errors.length} 个批次，${progress.errors.length} 个批次删除失败`
        }
        type={progress.errors.length === 0 ? "success" : "warning"}
        showIcon
        style={{ marginBottom: 16 }}
      />

      {progress.errors.length > 0 && (
        <Card title="失败详情" size="small">
          <List
            size="small"
            dataSource={progress.errors}
            renderItem={(error) => (
              <List.Item>
                <Text type="danger">
                  {error.batchId}: {error.error}
                </Text>
              </List.Item>
            )}
          />
        </Card>
      )}
    </div>
  );

  const getModalTitle = () => {
    switch (step) {
      case 'confirm':
        return (
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            批次删除确认
          </Space>
        );
      case 'progress':
        return (
          <Space>
            <ClockCircleOutlined style={{ color: '#1890ff' }} />
            正在删除批次
          </Space>
        );
      case 'result':
        return (
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            删除完成
          </Space>
        );
    }
  };

  const getFooter = () => {
    switch (step) {
      case 'confirm':
        return [
          <Button key="cancel" onClick={onCancel}>
            取消
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            icon={<DeleteOutlined />}
            loading={loading}
            disabled={!isConfirmValid()}
            onClick={handleConfirm}
          >
            确认删除
          </Button>
        ];
      case 'progress':
        return null; // 进度中不显示按钮
      case 'result':
        return [
          <Button key="close" type="primary" onClick={onCancel}>
            关闭
          </Button>
        ];
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={step === 'progress' ? undefined : onCancel}
      footer={getFooter()}
      width={800}
      closable={step !== 'progress'}
      maskClosable={false}
      destroyOnClose
    >
      {step === 'confirm' && renderConfirmStep()}
      {step === 'progress' && renderProgressStep()}
      {step === 'result' && renderResultStep()}
    </Modal>
  );
};

export default BatchDeleteConfirmModal;